import { useState } from "react";
import { Calculator, Check, Minus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatarPontos, valorCalculado } from "@/lib/campoCalculado/operadores";
import { useExplicacaoCampoCalculado } from "@/hooks/campoCalculado/useExplicacaoCampoCalculado";
import type { FieldDefinition } from "@/types/fieldRegistry";

interface Props {
  field: FieldDefinition;
  value: unknown;
  /** Sem o id do registro só mostra o número (sem a explicação). */
  recordId?: string;
  className?: string;
}

/**
 * Valor de um campo calculado: sempre somente leitura (quem grava é o trigger zzz_campo_calc).
 * Com `recordId`, clicar abre a composição dos pontos (RPC campo_calc_explicar).
 */
export function CampoCalculadoValor({ field, value, recordId, className }: Props) {
  const [aberto, setAberto] = useState(false);
  const numero = valorCalculado(value);
  const texto = numero === null ? "—" : numero.toLocaleString("pt-BR", { maximumFractionDigits: 2 });

  const conteudo = (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-sm font-medium tabular-nums",
        numero === null && "text-muted-foreground/50 italic text-xs font-normal",
        className,
      )}
    >
      <Calculator className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      {texto}
    </span>
  );

  if (!recordId) {
    return <span title="Campo calculado — somente leitura">{conteudo}</span>;
  }

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded px-1 -mx-1 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title="Ver como este valor foi calculado"
          onClick={(e) => e.stopPropagation()}
        >
          {conteudo}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <Explicacao fieldId={field.id} recordId={recordId} enabled={aberto} rotulo={field.field_label} />
      </PopoverContent>
    </Popover>
  );
}

function Explicacao({ fieldId, recordId, enabled, rotulo }: { fieldId: string; recordId: string; enabled: boolean; rotulo: string }) {
  const { data, isLoading, error } = useExplicacaoCampoCalculado(fieldId, recordId, enabled);

  if (isLoading) {
    return <p className="p-3 text-xs text-muted-foreground">Calculando…</p>;
  }
  if (error || !data) {
    return <p className="p-3 text-xs text-muted-foreground">Não foi possível carregar a composição deste valor.</p>;
  }

  const aguardando = valorCalculado(data.gravado) !== data.valor;

  return (
    <div className="text-sm">
      <div className="flex items-baseline justify-between gap-2 border-b px-3 py-2">
        <span className="font-medium truncate">{rotulo}</span>
        <span className="text-base font-semibold tabular-nums">
          {data.valor.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
        </span>
      </div>
      {data.regras.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">Nenhuma regra ativa.</p>
      ) : (
        <ul className="max-h-64 overflow-y-auto py-1">
          {data.regras.map((r) => (
            <li
              key={r.id}
              className={cn("flex items-center gap-2 px-3 py-1", !r.aplicou && "text-muted-foreground")}
            >
              {r.aplicou ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-label="aplicou" />
              ) : (
                <Minus className="h-3.5 w-3.5 shrink-0" aria-label="não aplicou" />
              )}
              <span className="flex-1 truncate">{r.nome}</span>
              <span className={cn("tabular-nums", !r.aplicou && "line-through")}>{formatarPontos(r.pontos)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="border-t px-3 py-2 text-xs text-muted-foreground">
        {aguardando
          ? "As regras mudaram e este registro ainda não foi recalculado. O valor acima é o que ele terá."
          : "Calculado automaticamente a partir das regras do campo. Não pode ser editado."}
      </p>
    </div>
  );
}
